/**
 * Convert a GeoJSON Polygon/MultiPolygon feature into Google Maps polygon
 * paths (arrays of {lat, lng}).
 *
 * Source data occasionally contains degenerate features — e.g. a ring whose
 * only coordinate is `[null]` (observed in polygons.geojson). Feeding such a
 * coordinate to `new google.maps.Polygon(...)` throws
 * "InvalidValueError: at index 0: ... in property lat: not a number" and
 * aborts layer loading. This helper drops non-finite coordinates and skips
 * rings with fewer than 3 valid points, mirroring the validation the CSV and
 * FAT-building render paths already perform.
 *
 * Behaviour is otherwise identical to the previous inline transform: only the
 * outer ring of each Polygon / each MultiPolygon member is used.
 *
 * @param {Object} feature - a GeoJSON Feature
 * @returns {Array<Array<{lat:number,lng:number}>>} valid ring paths (may be empty)
 */
export function featureToPolygonPaths(feature) {
  const geom = feature && feature.geometry;
  if (!geom) return [];

  let rawRings;
  if (geom.type === "Polygon") {
    rawRings = Array.isArray(geom.coordinates) ? [geom.coordinates[0]] : [];
  } else if (geom.type === "MultiPolygon") {
    rawRings = Array.isArray(geom.coordinates)
      ? geom.coordinates.map((poly) => (Array.isArray(poly) ? poly[0] : null))
      : [];
  } else {
    return [];
  }

  const paths = [];
  for (const ring of rawRings) {
    if (!Array.isArray(ring)) continue;

    const path = [];
    for (const coord of ring) {
      if (!Array.isArray(coord)) continue;
      const lng = coord[0];
      const lat = coord[1];
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        path.push({ lat, lng });
      }
    }

    // A renderable polygon ring needs at least 3 vertices.
    if (path.length >= 3) paths.push(path);
  }

  return paths;
}
